/**
 * Host allowlist for legacy `<iframe>` embeds.
 *
 * Iframes survive sanitization so legacy embeds keep rendering (see the rich-text
 * README), but they render on customer-facing estimate/invoice surfaces — so we
 * only trust a small, fixed set of embed providers. `rehype-canonicalize` calls
 * `isAllowedIframeHost` before it hardens an iframe: any src that doesn't resolve
 * to a trusted host is dropped subtree-and-all (like `<embed>`), never rendered.
 *
 * This is the layer that closes the same-origin sandbox-escape residual: an
 * iframe whose src is same-origin with the app can never be on a list of
 * third-party embed hosts, so it is dropped before it can load a frame that
 * removes its own sandbox.
 *
 * The list is evidence-based — every host except `vimeo.com` was observed in a
 * production embed scan (2026-09-02) across the six rich-text fields. Add a host
 * only when a real embed needs it; each entry is a domain we vouch to frame
 * content on a customer's estimate/invoice.
 */
export const IFRAME_HOST_ALLOWLIST = [
  // Video — YouTube stores most embeds protocol-relative (`//www.youtube.com/…`);
  // `iframeSrcHost` normalizes those to https before matching.
  "youtube.com",
  "youtube-nocookie.com", // privacy-preserving YouTube embed domain (pre-included)
  "vimeo.com", // supported video host (not seen in prod, but mainstream)
  "loom.com", // screen-recording walkthroughs
  // Documents — the single most common embed in prod is a published Google
  // Doc/Sheet. `docs.google.com` only (NOT all of google.com).
  "docs.google.com",
] as const;

/**
 * Resolve an iframe `src` to its lowercase hostname, or `null` if it isn't a
 * usable http(s) URL with a host.
 *
 * Handles the shapes legacy content actually stores:
 *   - `https://www.youtube.com/embed/x` -> "www.youtube.com"
 *   - `//www.youtube.com/embed/x`       -> "www.youtube.com" (protocol-relative)
 *   - `""` / whitespace / relative path -> null (no host to trust)
 *   - `javascript:…` / `data:…`         -> null (no host / non-http scheme)
 *
 * We enforce the http/https scheme here even though the sanitizer's protocol
 * allowlist also does — this runs first, and a host decision must not be made
 * from a non-navigational scheme.
 */
export function iframeSrcHost(src: unknown): string | null {
  if (typeof src !== "string") return null;
  const trimmed = src.trim();
  if (!trimmed) return null;

  // Protocol-relative (`//host/path`) inherits the page scheme at render time;
  // assume https so it parses to an absolute URL we can read a host from.
  const absolute = trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;

  let url: URL;
  try {
    url = new URL(absolute);
  } catch {
    // Relative path, anchor, malformed, or a scheme with no authority.
    return null;
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  return url.hostname.toLowerCase();
}

/**
 * Is this hostname a trusted iframe embed host?
 *
 * Matches an allowlist entry exactly, or as a dot-boundary subdomain
 * (`player.vimeo.com` matches `vimeo.com`). The dot boundary is what makes this
 * safe against the classic bypasses:
 *   - `evil-youtube.com`     — not equal, doesn't end with `.youtube.com` -> rejected
 *   - `youtube.com.evil.com` — ends with `.evil.com`, not `.youtube.com`  -> rejected
 */
export function isAllowedIframeHost(host: string | null | undefined): boolean {
  if (!host) return false;
  const h = host.toLowerCase();
  return IFRAME_HOST_ALLOWLIST.some(
    (allowed) => h === allowed || h.endsWith(`.${allowed}`),
  );
}

/** Convenience: does this raw iframe `src` resolve to a trusted embed host? */
export function isAllowedIframeSrc(src: unknown): boolean {
  return isAllowedIframeHost(iframeSrcHost(src));
}
